import { useState, useEffect, useCallback, createContext, useContext, useRef, useMemo } from 'react'
import { supabase, getCompanyId, clearCompanyIdCache, createCompanyForUser } from './supabase.js'
import { setCompanyIdCache, clearHelperCompanyIdCache } from './api/shared/helpers.js'
import { logger } from './logger.js'
import { getPasswordResetRedirectUrl } from '@prosperia/shared'
import { getPostSignInPath } from './postSignIn'

// Create authentication context
const SupabaseAuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  isAuthenticated: false,
  companyId: null,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  updateProfile: async () => {},
  resetPassword: async () => {},
  authReady: false,
  tokenError: false,
  emailVerified: false,
})

// Custom hook for using Supabase authentication
export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext)
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider')
  }
  return context
}

// Improved timeout logic
const AUTH_TIMEOUT = 60000 // 60 seconds
const COMPANY_ID_TIMEOUT = 30000 // 30 seconds
const SESSION_VALIDATION_COOLDOWN = 30000 // 30 seconds between validations
// SESSION_CHECK_INTERVAL removed - no longer using periodic health checks

// Authentication provider component
export const SupabaseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [companyId, setCompanyId] = useState(null)
  const [authReady, setAuthReady] = useState(false)
  const [authError, setAuthError] = useState(null)
  const [isSigningUp, setIsSigningUp] = useState(false)
  const [emailVerificationSent, setEmailVerificationSent] = useState(false)
  const [tokenError, setTokenError] = useState(false)
  const [emailVerified, setEmailVerified] = useState(null) // null = loading, true/false = known

  // Use refs to prevent re-initialization
  const initStartedRef = useRef(false)
  const lastSessionValidationRef = useRef(0)
  const authSubscriptionRef = useRef(null)
  const companyIdPromiseRef = useRef(null)
  const realtimeChannelRef = useRef(null)

  // Handle token errors by clearing corrupted auth data
  const handleTokenError = useCallback(async () => {
    console.warn('[useSupabaseAuth] Handling token error - clearing auth data')
    setTokenError(true)
    
    try {
      // Clear all auth-related data
      await supabase.auth.signOut()
      
      // Clear local storage
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.includes('supabase') || key.includes('auth')) {
          localStorage.removeItem(key)
        }
      })
      
      // Clear session storage
      sessionStorage.clear()
      
      // Reset state
      setUser(null)
      setSession(null)
      setCompanyId(null)
      setAuthError(null)
      setTokenError(false)
      
      console.log('[useSupabaseAuth] Auth data cleared, user needs to re-authenticate')
    } catch (error) {
      console.error('[useSupabaseAuth] Error clearing auth data:', error)
    }
  }, [])

  // Fetch email_verified from companies table and subscribe to realtime changes
  const fetchEmailVerified = useCallback(async (cId) => {
    try {
      const { data } = await supabase
        .from('companies')
        .select('email_verified')
        .eq('id', cId)
        .single()
      setEmailVerified(data?.email_verified ?? false)

      // Subscribe to realtime changes so UI updates when user verifies
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current)
      }
      const channel = supabase
        .channel(`email-verified-${cId}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'companies',
          filter: `id=eq.${cId}`,
        }, (payload) => {
          if (payload.new && typeof payload.new.email_verified === 'boolean') {
            setEmailVerified(payload.new.email_verified)
          }
        })
        .subscribe()
      realtimeChannelRef.current = channel
    } catch (err) {
      console.warn('[useSupabaseAuth] Failed to fetch email_verified:', err)
      setEmailVerified(false)
    }
  }, [])

  // Initialize authentication - FIXED to run only once
  useEffect(() => {
    // Prevent multiple initializations
    if (initStartedRef.current) {
      return
    }
    initStartedRef.current = true

    let mounted = true

    const initializeAuth = async () => {
      try {
        logger.auth('Starting auth initialization (once only)')
        
        // Get initial session with timeout
        logger.auth('Getting initial session...')
        
        // Attach a no-op .catch so that if Promise.race takes the timeout
        // branch and getSession() later rejects (e.g. AbortError from the
        // supabase-js navigator lock at locks.js:98), it doesn't become an
        // unhandled rejection that trips AuthErrorBoundary.
        const sessionPromise = supabase.auth.getSession().catch((err) => {
          if (err?.name !== 'AbortError') {
            console.warn('[useSupabaseAuth] getSession() rejected late:', err?.message || err)
          }
          return { data: null, error: err }
        })
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => {
            // Reduced timeout warning - only log occasionally
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.3) {
              console.warn('[useSupabaseAuth] getSession() timed out after 15 seconds')
            }
            reject(new Error('Session timeout'))
          }, 15000)
        )
        
        let sessionData, sessionError, sessionTimedOut = false
        try {
          const result = await Promise.race([sessionPromise, timeoutPromise])
          sessionData = result.data
          sessionError = result.error
          logger.auth('Session retrieved:', { hasSession: !!sessionData?.session })
        } catch (timeoutError) {
          console.warn('[useSupabaseAuth] Session retrieval timed out, continuing without session')
          sessionData = null
          sessionError = timeoutError
          sessionTimedOut = true
        }

        // A bare timeout / AbortError isn't a real auth failure — the session
        // may still be valid in storage; the navigator lock was just contended.
        // Treat it as "no session yet" and let onAuthStateChange populate it
        // when the lock frees up, rather than escalating to AuthErrorBoundary.
        const isTransientLockError =
          sessionTimedOut ||
          sessionError?.name === 'AbortError' ||
          sessionError?.message === 'Session timeout'

        if (sessionError && !isTransientLockError) {
          console.error('[useSupabaseAuth] Session error:', sessionError)

          // Check if it's a token-related error
          if (sessionError.message?.includes('Invalid Refresh Token') ||
              sessionError.message?.includes('Refresh Token Not Found') ||
              sessionError.message?.includes('JWT') ||
              sessionError.status === 400) {
            console.warn('[useSupabaseAuth] Token error detected, clearing auth data')
            await handleTokenError()
          }

          if (mounted) {
            setAuthError(sessionError)
            setLoading(false)
            setAuthReady(true)
          }
          return
        }

        // On a transient lock error we fall through with sessionData=null —
        // the no-initial-session branch below sets loading=false/authReady=true,
        // and the onAuthStateChange listener will populate the session later.

        const initialSession = sessionData?.session

        if (initialSession?.user) {
          logger.auth('Found initial session')
          if (mounted) {
            setSession(initialSession)
            setUser(initialSession.user)
            
            // Get company ID with timeout protection (no email_confirmed_at blocking)
            try {
              const companyIdPromise = Promise.race([
                getCompanyId(initialSession.user),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Company ID fetch timeout')), COMPANY_ID_TIMEOUT)
                )
              ])
              const resolvedCompanyId = await companyIdPromise
              if (mounted) {
                setCompanyId(resolvedCompanyId)
                // Sync to API helpers cache so API functions don't need to refetch
                if (resolvedCompanyId) {
                  setCompanyIdCache(resolvedCompanyId, initialSession.user.id)
                  // Fetch email_verified status
                  fetchEmailVerified(resolvedCompanyId)
                }
              }
            } catch (error) {
              console.warn('[useSupabaseAuth] Company ID fetch failed or timed out:', error.message)
              if (mounted) {
                setCompanyId(null)
              }
            } finally {
              if (mounted) {
                setLoading(false)
                setAuthReady(true)
              }
            }
          }
        } else {
          logger.auth('No initial session found')
          if (mounted) {
            setLoading(false)
            setAuthReady(true)
          }
        }
        
        // Ensure we always set loading to false and authReady to true
        if (mounted) {
          setLoading(false)
          setAuthReady(true)
        }

        // Set up auth state change listener (only once)
        if (!authSubscriptionRef.current) {
          authSubscriptionRef.current = supabase.auth.onAuthStateChange(async (event, newSession) => {
            if (!mounted) return
            
            logger.auth('Auth state changed:', event)
            
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              if (newSession?.user) {
                setSession(newSession)
                setUser(newSession.user)
                
                // Handle redirect after successful authentication - redirect all authenticated users from root to dashboard
                if (event === 'SIGNED_IN' && window.location.pathname === '/') {
                  console.log('[useSupabaseAuth] User signed in, redirecting')
                  // Small delay to ensure state is updated
                  const destination = getPostSignInPath(newSession.user)
                  setTimeout(() => {
                    window.location.replace(destination)
                  }, 100)
                }
                
                // Check if user needs company creation
                if (newSession.user.user_metadata?.pending_company_creation) {
                  try {
                    console.log('[useSupabaseAuth] Creating company for verified user')
                    await createCompanyForUser(newSession.user)
                  } catch (error) {
                    console.error('[useSupabaseAuth] Error creating company:', error)
                  }
                }

                // Auto-activate employee if they're invited/pending (only on SIGNED_IN).
                // Not on /reset-password: an invite link signs the user in before
                // they have a password, and ResetPassword activates after it's set (#630).
                if (event === 'SIGNED_IN' && window.location.pathname !== '/reset-password') {
                  try {
                    const { data: activated, error: activateError } = await supabase.rpc('activate_employee')
                    if (activateError) {
                      if (process.env.NODE_ENV === 'development') {
                        console.log('[useSupabaseAuth] Employee activation check:', activateError.message)
                      }
                    } else if (activated) {
                      console.log('[useSupabaseAuth] Employee auto-activated on sign in')
                    }
                  } catch (err) {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[useSupabaseAuth] Employee activation check failed:', err)
                    }
                  }
                }

                // Only fetch company ID if we don't have it and no request is pending
                if (!companyId && newSession.user.id !== user?.id && !companyIdPromiseRef.current) {
                  companyIdPromiseRef.current = Promise.race([
                    getCompanyId(newSession.user),
                    new Promise((_, reject) =>
                      setTimeout(() => reject(new Error('Company ID fetch timeout')), 15000)
                    )
                  ])
                  try {
                    const resolvedCompanyId = await companyIdPromiseRef.current
                    if (mounted) {
                      setCompanyId(resolvedCompanyId)
                      if (resolvedCompanyId) {
                        setCompanyIdCache(resolvedCompanyId, newSession.user.id)
                        fetchEmailVerified(resolvedCompanyId)
                      }
                    }
                  } catch (error) {
                    console.warn('[useSupabaseAuth] Company ID fetch failed or timed out:', error)
                    if (mounted) {
                      setCompanyId(null)
                    }
                  } finally {
                    companyIdPromiseRef.current = null
                  }
                }
                
                if (mounted) {
                  setLoading(false)
                  setAuthReady(true)
                }
              }
            } else if (event === 'SIGNED_OUT') {
              setSession(null)
              setUser(null)
              setCompanyId(null)
              setEmailVerificationSent(false)
              companyIdPromiseRef.current = null
              setLoading(false)
              setAuthReady(true)
            }
          })
        }

        // PERFORMANCE FIX: Removed periodic health check - Supabase handles token refresh automatically
        // The previous 60-second interval health check was making 1 API call/minute/user unnecessarily
        // Supabase's built-in onAuthStateChange listener already handles token refresh and session validation
        // Removing this saves ~1,440 API calls per user per day

      } catch (error) {
        console.error('[useSupabaseAuth] Init error:', error)
        console.error('[useSupabaseAuth] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: error.cause
        })
        
        // Always ensure the app loads, even if there's an error
        console.log('[useSupabaseAuth] Error occurred, but ensuring app loads anyway')
        if (mounted) {
          setAuthError(error)
          setLoading(false)
          setAuthReady(true)
        }
      }
    }

    initializeAuth()

    // Cleanup function
    return () => {
      mounted = false

      if (authSubscriptionRef.current?.unsubscribe) {
        authSubscriptionRef.current.unsubscribe()
        authSubscriptionRef.current = null
      }

      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current)
        realtimeChannelRef.current = null
      }
    }
  }, []) // Empty dependency array - runs only once

  // Sign in method
  const signIn = useCallback(async (email, password) => {
    try {
      setLoading(true)
      setIsSigningUp(false)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      if (error) throw error
      
      if (data.user && data.session) {
        setUser(data.user)
        setSession(data.session)
        
        // Track successful sign in
        try {
          const { analytics } = await import('./analytics')
          analytics.trackSignIn('email')
        } catch (err) {
          // Analytics import failed, continue without tracking
        }
      }
      
      return { success: true, data }
    } catch (error) {
      console.error('[useSupabaseAuth] Sign in error:', error)
      return { success: false, error: error.message, code: error.code }
    } finally {
      setLoading(false)
    }
  }, [])

  // Sign up method
  const signUp = useCallback(async (email, password, userData = {}) => {
    try {
      setLoading(true)
      setIsSigningUp(true)
      setEmailVerificationSent(true)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })
      
      if (error) throw error
      
      if (data.user) {
        setUser(data.user)
        if (data.session) {
          setSession(data.session)
        }
        
        // Track successful sign up
        try {
          const { analytics } = await import('./analytics')
          analytics.trackSignUp(userData.signup_source || 'website')
        } catch (err) {
          // Analytics import failed, continue without tracking
        }
        
        // Track Meta Pixel sign up event
        try {
          const { trackMetaPixelSignUp } = await import('./metaPixel')
          trackMetaPixelSignUp({
            source: userData.signup_source || 'website',
            email: email
          })
        } catch (err) {
          // Meta Pixel import failed, continue without tracking
        }
      }
      
      return { success: true, data }
    } catch (error) {
      console.error('[useSupabaseAuth] Sign up error:', error)
      setIsSigningUp(false)
      setEmailVerificationSent(false)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }, [])

  // Sign out method
  const signOut = useCallback(async () => {
    try {
      setLoading(true)
      setIsSigningUp(false)
      setEmailVerificationSent(false)
      
      if (user?.id) {
        clearCompanyIdCache(user.id)
        clearHelperCompanyIdCache() // Clear the main API cache too
      }
      
      const { error } = await supabase.auth.signOut()
      
      if (error) throw error
      
      // Track sign out
      try {
        const { analytics } = await import('./analytics')
        analytics.trackSignOut()
      } catch (err) {
        // Analytics import failed, continue without tracking
      }
      
      setUser(null)
      setSession(null)
      setCompanyId(null)
      setEmailVerified(null)
      companyIdPromiseRef.current = null

      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current)
        realtimeChannelRef.current = null
      }

      return { success: true }
    } catch (error) {
      console.error('[useSupabaseAuth] Sign out error:', error)
      return { success: false, error: error.message }
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    try {
      if (!user) throw new Error('No user authenticated')
      
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })
      
      if (error) throw error
      
      setUser(data.user)
      
      return { success: true, data }
    } catch (error) {
      console.error('[useSupabaseAuth] Profile update error:', error)
      return { success: false, error: error.message }
    }
  }, [user])

  // Refresh user + companyId from Supabase (used after first-login company creation)
  const refreshCompanyId = useCallback(async () => {
    try {
      const { data: refreshed } = await supabase.auth.getUser()
      if (refreshed?.user) {
        setUser(refreshed.user)
      }
      const targetUser = refreshed?.user || user
      if (!targetUser) return null

      clearCompanyIdCache(targetUser.id)
      clearHelperCompanyIdCache()

      const resolved = await getCompanyId(targetUser)
      setCompanyId(resolved)
      if (resolved) {
        setCompanyIdCache(resolved, targetUser.id)
        fetchEmailVerified(resolved)
      }
      return resolved
    } catch (error) {
      console.error('[useSupabaseAuth] refreshCompanyId error:', error)
      return null
    }
  }, [user, fetchEmailVerified])

  // Reset password
  const resetPassword = useCallback(async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getPasswordResetRedirectUrl(window.location.origin)
      })
      
      if (error) throw error
      
      return { success: true }
    } catch (error) {
      console.error('[useSupabaseAuth] Password reset error:', error)
      return { success: false, error: error.message }
    }
  }, [])

  // Send verification email via edge function
  const resendConfirmation = useCallback(async (email) => {
    try {
      if (!companyId) throw new Error('Company not found')

      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession?.access_token) throw new Error('No active session')

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-verification-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ email, companyId }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to send verification email')
      }

      return { success: true }
    } catch (error) {
      console.error('[useSupabaseAuth] Resend verification error:', error)
      return { success: false, error: error.message }
    }
  }, [companyId])

  // Optional onboarding functionality removed

  // Calculate authentication state (no email_confirmed_at blocking)
  const isAuthenticated = useMemo(() => {
    return !!user && !!session
  }, [user, session])

  // Optional onboarding check removed

  // Context value
  const value = useMemo(() => ({
    user,
    session,
    loading,
    isAuthenticated,
    companyId,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    resendConfirmation,
    refreshCompanyId,
    authReady,
    authError,
    tokenError,
    isSigningUp,
    emailVerificationSent,
    needsEmailVerification: !!user && emailVerified === false,
    emailVerified, // null = loading, true = verified, false = not verified
  }), [
    user,
    session,
    loading,
    isAuthenticated,
    companyId,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword,
    resendConfirmation,
    refreshCompanyId,
    authReady,
    authError,
    tokenError,
    isSigningUp,
    emailVerificationSent,
    emailVerified,
  ])

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  )
}

// Export hooks and aliases
export const useAuthStatus = () => {
  const { isAuthenticated, loading, authReady } = useSupabaseAuth()
  
  return {
    isAuthenticated,
    loading,
    authReady,
    isReady: authReady && !loading
  }
}

export const useCompany = () => {
  const { companyId, user } = useSupabaseAuth()
  
  return {
    companyId,
    user,
    hasCompany: !!companyId,
    isCompanyUser: !!companyId && !!user
  }
}

export const useUserProfile = () => {
  const { user, updateProfile } = useSupabaseAuth()
  
  const updateUserProfile = useCallback(async (updates) => {
    if (!user) return { success: false, error: 'No user authenticated' }
    
    return await updateProfile(updates)
  }, [user, updateProfile])
  
  return {
    user,
    profile: user?.user_metadata || {},
    updateProfile: updateUserProfile,
    canUpdate: !!user
  }
}

// Export context for SSR stub provider
export { SupabaseAuthContext }

// Aliases for backward compatibility
export const useAuth = useSupabaseAuth
export const AuthProvider = SupabaseAuthProvider

export const useRequireAuth = (redirectTo = '/login') => {
  const { user, loading } = useSupabaseAuth()
  
  useEffect(() => {
    if (!loading && !user) {
      window.location.href = redirectTo
    }
  }, [user, loading, redirectTo])
  
  return { user, loading, isAuthenticated: !!user }
}

export default useSupabaseAuth